import React, { useState, useEffect, useRef } from "react";
import styles from "./CallSetting.module.css";
import axios from "axios";
import { API_BASE_URL, token, updateAgentFromBackend } from "../../Store/apiStore";

export default function ListeningAffirmations() {
  const [interruptionSensitivity, setInterruptionSensitivity] = useState(0.91);
  const [responsiveness, setResponsiveness] = useState(1);
  const [voiceSpeed, setVoiceSpeed] = useState(1);
  const [saving, setSaving] = useState(false);
  // Which slider is hovered — drives the Min/Max hint visibility (desktop hover).
  const [hovered, setHovered] = useState(null);

  // Same id the rest of Call Setting fetches with.
  const resolvedAgentId = sessionStorage.getItem("SelectAgentId");

  // Fetch agent details from API
  useEffect(() => {
    if (resolvedAgentId) {
      const fetchAgentDetails = async () => {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/agent/getAgent/${resolvedAgentId}` ,
             { headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },}
          );
          // console.log("Fetched agent details:", res.data);
          const { interruption_sensitivity, responsiveness, voice_speed } = res.data;
          setInterruptionSensitivity(interruption_sensitivity || 0.91);
          // Slider range here is 0.1–1; clamp so a larger stored value never overflows.
          setResponsiveness(Math.min(responsiveness || 1, 1));
          setVoiceSpeed(voice_speed || 1);
        } catch (error) {
          console.error("Error fetching agent details:", error);
        }
      };

      fetchAgentDetails();
    }
  }, [resolvedAgentId]);

  // Commit a slider value to the agent. DEBOUNCED (~400ms after the last change)
  // and the sliders are NEVER disabled — disabling mid-drag is what made them
  // feel "stuck" when the user moved them rapidly.
  const commitTimers = useRef({});
  const commit = (field, value) => {
    if (!resolvedAgentId) return;
    clearTimeout(commitTimers.current[field]);
    commitTimers.current[field] = setTimeout(async () => {
      try {
        setSaving(true);
        await updateAgentFromBackend({ agent_id: resolvedAgentId, [field]: value });
      } catch (error) {
        console.error(`Failed to update ${field}:`, error);
      } finally {
        setSaving(false);
      }
    }, 400);
  };

  return (
    <div>
      <div className={styles.toggleRow}>
        <label className={styles.label}>Interruption Sensitivity</label>
      </div>
      <p className={styles.description}>
       How quickly the agent stops when user talks over it.
      </p>

      <div
        className={styles.sliderTop}
        onPointerEnter={() => setHovered("interrupt")}
        onPointerLeave={() => setHovered(null)}
      >
        <div className={styles.sliderContainer}>
          <div className={styles.sliderWrapper}>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={interruptionSensitivity}
              onChange={(e) =>
                setInterruptionSensitivity(parseFloat(e.target.value))
              }
              onMouseUp={(e) =>
                commit("interruption_sensitivity", parseFloat(e.target.value))
              }
              onTouchEnd={(e) =>
                commit("interruption_sensitivity", parseFloat(e.target.value))
              }
              className={styles.sliderRange}
              style={{
                background: `linear-gradient(to right, #693bff ${
                  ((interruptionSensitivity - 0.1) / 0.9) * 100
                }%, #fff ${((interruptionSensitivity - 0.1) / 0.9) * 100}%)`,
                cursor: "pointer",
              }}
            />
            <span className={styles.sliderValue}>
              {interruptionSensitivity.toFixed(1)}
            </span>
          </div>
          <div
            className={`${styles.minMaxRow} ${
              hovered === "interrupt" ? styles.minMaxRowVisible : ""
            }`}
          >
            <span>Low</span>
            <span>High</span>
          </div>
        </div>
      </div>

      {/* Response Eagerness */}
      <div className={` ${styles.toggleRow} ${styles.toggleRow1} `}>
        <label className={styles.label}>Response Eagerness</label>
      </div>
      <p className={styles.description}>
        How quickly the agent starts responding after the user finishes.
      </p>

      <div
        className={styles.sliderTop}
        onPointerEnter={() => setHovered("eager")}
        onPointerLeave={() => setHovered(null)}
      >
        <div className={styles.sliderContainer}>
          <div className={styles.sliderWrapper}>
            <input
              type="range"
              min="0.1"
              max="1"
              step="0.1"
              value={responsiveness}
              onChange={(e) => setResponsiveness(parseFloat(e.target.value))}
              onMouseUp={(e) =>
                commit("responsiveness", parseFloat(e.target.value))
              }
              onTouchEnd={(e) =>
                commit("responsiveness", parseFloat(e.target.value))
              }
              className={styles.sliderRange}
              style={{
                background: `linear-gradient(to right, #693bff ${
                  ((responsiveness - 0.1) / 0.9) * 100
                }%, #fff ${((responsiveness - 0.1) / 0.9) * 100}%)`,
                cursor: "pointer",
              }}
            />
            <span className={styles.sliderValue}>
              {responsiveness.toFixed(1)}
            </span>
          </div>
          <div
            className={`${styles.minMaxRow} ${
              hovered === "eager" ? styles.minMaxRowVisible : ""
            }`}
          >
            <span>Patient</span>
            <span>Eager</span>
          </div>
        </div>
      </div>

      {/* Voice Speed */}
      <div className={` ${styles.toggleRow} ${styles.toggleRow1} `}>
        <label className={styles.label}>Voice Speed</label>
      </div>
      <p className={styles.description}>
        Control how fast the agent speaks.
      </p>

      <div
        className={styles.sliderTop}
        onPointerEnter={() => setHovered("speed")}
        onPointerLeave={() => setHovered(null)}
      >
        <div className={styles.sliderContainer}>
          <div className={styles.sliderWrapper}>
            <input
              type="range"
              min="0.5"
              max="2"
              step="0.01"
              value={voiceSpeed}
              onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))}
              onMouseUp={(e) =>
                commit("voice_speed", parseFloat(e.target.value))
              }
              onTouchEnd={(e) =>
                commit("voice_speed", parseFloat(e.target.value))
              }
              className={styles.sliderRange}
              style={{
                background: `linear-gradient(to right, #693bff ${
                  ((voiceSpeed - 0.5) / 1.5) * 100
                }%, #fff ${((voiceSpeed - 0.5) / 1.5) * 100}%)`,
                cursor: "pointer",
              }}
            />
            <span className={styles.sliderValue}>
              {voiceSpeed.toFixed(2)}
            </span>
          </div>
          <div
            className={`${styles.minMaxRow} ${
              hovered === "speed" ? styles.minMaxRowVisible : ""
            }`}
          >
            <span>Slow</span>
            <span>Fast</span>
          </div>
        </div>
      </div>
    </div>
  );
}
